import { View, ViewOpts, animate } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { clamp } from 'src/replicant/utils';
import { getRescueLevelTheme } from '../rescueLevel.theme';
import gsap from 'gsap';

export class GoalProgress extends View {
  private progressBar: ProgressBar;
  private label: LangBitmapFontTextView;
  private value = 0;
  public readonly icon: View;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
    });

    this.progressBar = new ProgressBar({
      superview: this,
      bgStyle: {
        image: 'assets/minigame/rescueLevel/progress_bg.png',
        width: downscale(405),
        height: downscale(123),
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 60, right: 60 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
      },
      fillStyle: {
        image: 'assets/minigame/rescueLevel/progress_fill.png',
        width: downscale(387),
        height: downscale(105),
        x: 6,
        y: 5,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 30, right: 30 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
        paddingRight: 12,
      },
      minWidth: opts.minWidth ?? 50,
      domain: [0, 1],
      progress: 0,
    });

    this.label = new LangBitmapFontTextView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      size: 36,
      font: 'Title',
      width: 180,
      height: 80,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.progressBar.style.width / 2,
      y: this.progressBar.style.height / 2 - 3,
      localeText: () => '00/00',
    });

    this.icon = getRescueLevelTheme().getIconSmall({
      superview: this,
      x: this.progressBar.style.width + 20, // 20px to the right to not cover the bar too much
      y: this.progressBar.style.height / 2,
      centerOnOrigin: true,
    });
  }

  public setProgress(value: number, total: number) {
    if (total <= 0) throw new Error('total must be greater than 0');

    // Clamp the progress value between 0 and total just in case
    value = clamp(value, 0, total);

    // Update the progress text
    this.label.localeText = () => `${Math.floor(value)}/${Math.floor(total)}`;

    // Update the progress bar
    const progress = clamp(value / total, 0, 1);
    this.progressBar.progress = progress;
  }

  public animateProgress(value: number, total: number) {
    total = Math.floor(total);
    if (total <= 0)
      throw new Error(
        `GoalProgress.animateProgress: total must be greater than 0, got ${total}`,
      );

    // Clamp the progress value between 0 and total just in case
    value = clamp(value, 0, total);

    const duration = 0.3;

    // Update the progress text
    gsap.killTweensOf(this);
    gsap.to(this, {
      value: value,
      duration,
      ease: 'linear',
      onUpdate: () => {
        const current = Math.round(this.value);
        this.label.localeText = () => `${current}/${total}`;
      },
      onComplete: () => {
        const current = Math.round(value);
        this.label.localeText = () => `${current}/${total}`;
      },
    });

    // Update the progress bar
    const progress = clamp(value / total, 0, 1);
    this.progressBar.animateProgress(
      progress,
      duration * 1000,
      animate.linear,
      true,
    );
  }
}
